import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Phone, MessageSquare, ShieldAlert, CheckCircle2, Clock, User, Truck, Hand, Radio } from 'lucide-react';
import type { AlertEvent } from '../../types/alert';
import { SEVERITY_LABELS, SEVERITY_COLORS, CATEGORY_LABELS, ALERT_SOURCE_LABELS } from '../../utils/constants';
import { formatDate, formatRelative } from '../../utils/formatters';
import { EmergencyCallModal } from './EmergencyCallModal';
import { AlertEvidenceList } from './AlertEvidenceList';
import { useDrivers } from '../../hooks/useDrivers';

interface AlertExpandedModalProps {
  isOpen: boolean;
  onClose: () => void;
  alert: AlertEvent | null;
  onResolveAlert: (id: string, reason: string) => void;
}

const CLOSURE_REASONS = [
  'Verificado con Conductor — Falsa Alerta',
  'Atendido por Supervisor — Protocolo Aplicado',
  'Falsa Alerta / Calibración Sensor',
  'Mantenimiento Programado Solicitado',
  'Novedad Gestionada y Documentada',
];

export function AlertExpandedModal({ isOpen, onClose, alert, onResolveAlert }: AlertExpandedModalProps) {
  const [showCallModal, setShowCallModal] = useState(false);
  const [selectedReason, setSelectedReason] = useState(CLOSURE_REASONS[0]);
  const [notes, setNotes] = useState('');
  const { drivers } = useDrivers();
  const driverPhone = drivers.find(d => d.id === alert?.driverId)?.phone;

  if (!alert) return null;

  const color = SEVERITY_COLORS[alert.severity];

  const handleResolve = (e: React.FormEvent) => {
    e.preventDefault();
    const fullReason = notes.trim() ? `${selectedReason}: ${notes.trim()}` : selectedReason;
    onResolveAlert(alert.id, fullReason);
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`🚨 Detalle & Gestión de Alerta: #${alert.id.toUpperCase()}`} size="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Header Banner */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: `${color}15`, borderLeft: `4px solid ${color}`, borderRadius: 'var(--radius-lg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Badge variant={alert.severity === 'critical' ? 'critical' : alert.severity === 'medium' ? 'medium' : 'low'}>
                {SEVERITY_LABELS[alert.severity]}
              </Badge>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: 'var(--text-primary)' }}>
                  {CATEGORY_LABELS[alert.category]} {alert.subCategory ? `— ${alert.subCategory}` : ''}
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Registrado {formatRelative(alert.timestamp)}</span>
              </div>
            </div>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '99px', background: 'var(--bg-raised)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
              Sub-unidad: {alert.subUnitName}
            </span>
          </div>

          {/* Grid Layout: Image Preview + Telemetry Meta */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
            
            {/* Left: evidencia real (BE-52) — ver AlertEvidenceList */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: 'var(--text-secondary)' }}>📷 Evidencia en video/imagen</p>
              <AlertEvidenceList alertId={alert.id} fileStem={`evidencia-${alert.vehiclePlate}`} />
            </div>

            {/* Right: Telemetry Metadata */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', background: 'var(--bg-raised)', padding: '1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
              <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>📊 Datos Telemetría & Conductor</p>

              {/* FE-17/BE-45: `sos + device` es el botón físico del equipo;
                  `sos + human`, el botón de pánico de la app. Sin esta fila las
                  dos se leen igual. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '13px' }}>
                {alert.source === 'human'
                  ? <Hand size={16} style={{ color: '#D97706' }} />
                  : <Radio size={16} style={{ color: 'var(--accent)' }} />}
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Procedencia</p>
                  <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{ALERT_SOURCE_LABELS[alert.source]}</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '13px' }}>
                <Truck size={16} style={{ color: 'var(--accent)' }} />
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Vehículo Placa</p>
                  <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{alert.vehiclePlate}</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '13px' }}>
                <User size={16} style={{ color: 'var(--accent)' }} />
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Conductor Involucrado</p>
                  <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{alert.driverName || 'Sin asignar'}</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', fontSize: '13px' }}>
                <Clock size={16} style={{ color: 'var(--accent)' }} />
                <div>
                  <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Fecha y Hora Exacta</p>
                  <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{formatDate(alert.timestamp)}</p>
                </div>
              </div>

              {/* Instant Actions */}
              {alert.driverName && alert.status === 'active' && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowCallModal(true)}
                    leftIcon={<Phone size={14} />}
                    style={{ flex: 1, background: '#10B981', borderColor: '#10B981' }}
                  >
                    Llamar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('open-chat', { detail: { driverId: alert.driverId, driverName: alert.driverName } }));
                      onClose();
                    }}
                    leftIcon={<MessageSquare size={14} />}
                    style={{ flex: 1 }}
                  >
                    Ir al Chat
                  </Button>
                </div>
              )}
            </div>

          </div>

          {/* Closure Form for Supervisor */}
          {alert.status === 'active' ? (
            <form onSubmit={handleResolve} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--accent)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--accent)', fontWeight: 700, fontSize: '13px' }}>
                <ShieldAlert size={16} />
                <span>Formulario de Resolución y Cierre Documentado</span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Motivo de Resolución
                  </label>
                  <select
                    value={selectedReason}
                    onChange={e => setSelectedReason(e.target.value)}
                    style={{
                      width: '100%', padding: '0.625rem 0.875rem', background: 'var(--bg-raised)',
                      color: 'var(--text-primary)', border: '1px solid var(--border-strong)',
                      borderRadius: 'var(--radius-md)', fontSize: '13px', cursor: 'pointer'
                    }}
                  >
                    {CLOSURE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Observaciones Adicionales
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. Se verificó con el conductor vía telefónica"
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    style={{
                      width: '100%', padding: '0.625rem 0.875rem', background: 'var(--bg-raised)',
                      color: 'var(--text-primary)', border: '1px solid var(--border-strong)',
                      borderRadius: 'var(--radius-md)', fontSize: '13px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
                <Button variant="ghost" size="sm" type="button" onClick={onClose}>Cancelar</Button>
                <Button variant="danger" size="sm" type="submit" leftIcon={<CheckCircle2 size={15} />}>
                  Confirmar y Dar Cierre a Alerta
                </Button>
              </div>
            </form>
          ) : (
            <div style={{ padding: '0.875rem 1rem', background: 'rgba(16,185,129,0.12)', border: '1px solid #10B981', borderRadius: 'var(--radius-lg)', color: '#10B981', fontSize: '13px' }}>
              ✓ Alerta resuelta y cerrada por <strong>{alert.closedBy || 'Supervisor'}</strong>: "{alert.closureReason}"
            </div>
          )}

        </div>
      </Modal>

      {/* Emergency Call Modal */}
      <EmergencyCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        driverName={alert.driverName || 'Conductor'}
        driverPhone={driverPhone}
        vehiclePlate={alert.vehiclePlate}
      />
    </>
  );
}
